import { Generations } from '@pkmn/data';
import { Dex } from '@pkmn/dex';
import type { Result } from '@smogon/calc';
import { calculate, Field, Move, Pokemon, Side } from '@smogon/calc';

const gen = new Generations(Dex).get(9);

export type Stat = 'hp' | 'atk' | 'def' | 'spa' | 'spd' | 'spe';
export type PartialStats = Partial<Record<Stat, number>>;

export type PokemonSpec = {
  species: string;
  level?: number;
  item?: string;
  ability?: string;
  nature?: string;
  evs?: PartialStats;
  ivs?: PartialStats;
  boosts?: PartialStats;
  status?: string;
  teraType?: string;
  // Optional baseStats / types override — used by the Champions mega overlay
  // (`src/calc/champions-megas.ts`) to give @smogon/calc the mega form's
  // stats and types without a Dex extension. Passed through verbatim to the
  // Pokemon constructor's `overrides` field.
  overrides?: {
    baseStats?: { hp: number; atk: number; def: number; spa: number; spd: number; spe: number };
    types?: string[];
  };
};

export type SideSpec = {
  isReflect?: boolean;
  isLightScreen?: boolean;
  isAuroraVeil?: boolean;
  isFriendGuard?: boolean;
  isHelpingHand?: boolean;
};

export type FieldSpec = {
  weather?: 'Sun' | 'Rain' | 'Sand' | 'Snow' | 'Hail' | 'Harsh Sunshine' | 'Heavy Rain';
  terrain?: 'Electric' | 'Grassy' | 'Misty' | 'Psychic';
  isGravity?: boolean;
  // Default is 'Doubles' (VGC). Set 'Singles' explicitly when modeling a
  // 1-on-1 (e.g. last-mon scenarios). Doubles applies the 0.75x multiplier
  // automatically for moves @smogon/calc flags as spread (Earthquake,
  // Rock Slide, Surf, Heat Wave, Dazzling Gleam, Discharge, etc.).
  gameType?: 'Singles' | 'Doubles';
  attackerSide?: SideSpec;
  defenderSide?: SideSpec;
};

export type CalcResult = {
  damage: { min: number; max: number; minPct: number; maxPct: number };
  koChance: { text: string; chance: number };
  desc: string;
  rolls: readonly number[];
  isOhko: boolean;
  // Deterministic type-effectiveness derived from the move's type vs the
  // defender's effective types (Tera type when terastallized, else the
  // resolved species' types — including the Champions mega overlay). The
  // agent reads this verbatim instead of deriving the matchup from
  // training-data type-chart knowledge, which has empirically been wrong
  // (e.g. claiming Solar Beam is resisted by Milotic).
  effectiveness: {
    multiplier: number;
    label: 'immune' | 'resisted' | 'neutral' | 'super_effective';
    moveType: string;
    defenderTypes: readonly string[];
  };
  // Move metadata read off @pkmn/dex — surfaced so the agent doesn't narrate
  // BP / priority / contact / sound / spread / accuracy from training data
  // (which is mainline Gen 9 and may not match Champions). Action-changing
  // claims like "Sucker Punch has +1 priority" must read from this field.
  // Distinct from `move` at the agent-tool-result level, which is the
  // string display name passed back to the card.
  moveData: {
    name: string;
    type: string;
    basePower: number;
    priority: number;
    accuracy: number | true;
    target: string;
    isSpread: boolean;
    isContact: boolean;
    isSound: boolean;
    isPunch: boolean;
    isBite: boolean;
    isSlicing: boolean;
    isMultihit: boolean;
    multihit?: number | readonly number[];
    secondaryChancePct?: number;
  };
  // Echo of what was actually passed to @smogon/calc — so the agent can't
  // narrate "in sun this 2HKOs" unless appliedField.weather === 'Sun'. Empty
  // strings instead of undefined so the agent sees the field explicitly.
  appliedField: {
    weather: string;
    terrain: string;
    gameType: 'Singles' | 'Doubles';
    attackerSide: {
      reflect: boolean;
      lightScreen: boolean;
      auroraVeil: boolean;
      friendGuard: boolean;
      helpingHand: boolean;
    };
    defenderSide: {
      reflect: boolean;
      lightScreen: boolean;
      auroraVeil: boolean;
      friendGuard: boolean;
      helpingHand: boolean;
    };
  };
  appliedStatus: {
    attacker: string;
    defender: string;
  };
  raw: Result;
};

function buildPokemon(spec: PokemonSpec): Pokemon {
  return new Pokemon(gen, spec.species, {
    level: spec.level ?? 50,
    item: spec.item,
    ability: spec.ability,
    nature: spec.nature,
    evs: spec.evs,
    ivs: spec.ivs,
    boosts: spec.boosts,
    status: spec.status as any,
    teraType: spec.teraType as any,
    overrides: spec.overrides as any,
  });
}

function buildField(spec?: FieldSpec): Field {
  // VGC is doubles by default. Spread moves (Earthquake, Rock Slide, Surf,
  // Heat Wave, etc.) get 0.75x damage automatically when gameType='Doubles'
  // and @smogon/calc has the move's `isSpread` metadata. Pass `singles` only
  // when the caller is explicitly modeling a last-mon-standing scenario.
  return new Field({
    gameType: spec?.gameType ?? 'Doubles',
    weather: spec?.weather as any,
    terrain: spec?.terrain as any,
    isGravity: spec?.isGravity,
    attackerSide: spec?.attackerSide ? new Side(spec.attackerSide) : undefined,
    defenderSide: spec?.defenderSide ? new Side(spec.defenderSide) : undefined,
  });
}

export function calcDamage(
  attackerSpec: PokemonSpec,
  defenderSpec: PokemonSpec,
  moveName: string,
  fieldSpec?: FieldSpec,
): CalcResult {
  const attacker = buildPokemon(attackerSpec);
  const defender = buildPokemon(defenderSpec);
  const move = new Move(gen, moveName);
  const field = buildField(fieldSpec);
  const result = calculate(gen, attacker, defender, move, field);

  const [minDmg, maxDmg] = result.range();
  const hp = defender.maxHP();
  // @smogon/calc's kochance() throws on zero-damage rolls (its internal
  // util guards against an all-zero damage array). For type immunities
  // (EQ vs Flying, normal vs Ghost, etc.) we just want to say "immune"
  // rather than blow up the whole call — short-circuit here.
  const koChance: { text: string; chance: number } =
    maxDmg === 0
      ? { text: 'immune', chance: 0 }
      : (() => {
          const k = result.kochance();
          return { text: k.text, chance: k.chance ?? 0 };
        })();

  // `result.damage` is the 16-element damage rolls array for standard moves.
  // Multi-hit and a few special-cased moves return a 2D structure; out-of-scope
  // until a caller needs them.
  const rolls = Array.isArray(result.damage) ? (result.damage as number[]) : [minDmg, maxDmg];
  // result.desc() internally calls getKOChance and throws on zero damage —
  // same workaround as koChance above: provide a sensible immune description
  // instead of bubbling the throw.
  const desc =
    maxDmg === 0
      ? `${attackerSpec.species} ${moveName} vs. ${defenderSpec.species}: 0 damage (immune)`
      : result.desc();

  return {
    damage: {
      min: minDmg,
      max: maxDmg,
      minPct: (minDmg / hp) * 100,
      maxPct: (maxDmg / hp) * 100,
    },
    koChance,
    desc,
    rolls,
    isOhko: maxDmg >= hp,
    effectiveness: computeEffectiveness(move, defender, defenderSpec),
    moveData: extractMoveMetadata(moveName),
    appliedField: extractAppliedField(fieldSpec),
    appliedStatus: {
      attacker: String(attackerSpec.status ?? ''),
      defender: String(defenderSpec.status ?? ''),
    },
    raw: result,
  };
}

// Surface @pkmn/dex move data so the agent narrates BP / priority / contact
// flags / spread / accuracy off this field rather than from training data.
// Spread is derived from target: 'allAdjacentFoes' (Heat Wave) and
// 'allAdjacent' (Earthquake, Surf) take the 0.75× multiplier in doubles.
function extractMoveMetadata(moveName: string): CalcResult['moveData'] {
  const m = Dex.forGen(9).moves.get(moveName);
  const flags = (m.flags ?? {}) as Record<string, number>;
  return {
    name: m.name ?? moveName,
    type: String(m.type ?? ''),
    basePower: Number(m.basePower ?? 0),
    priority: Number(m.priority ?? 0),
    accuracy: m.accuracy as number | true,
    target: String(m.target ?? ''),
    isSpread: m.target === 'allAdjacentFoes' || m.target === 'allAdjacent',
    isContact: Boolean(flags.contact),
    isSound: Boolean(flags.sound),
    isPunch: Boolean(flags.punch),
    isBite: Boolean(flags.bite),
    isSlicing: Boolean(flags.slicing),
    isMultihit: Boolean((m as any).multihit),
    multihit: (m as any).multihit,
    secondaryChancePct: ((m as any).secondary?.chance as number | undefined) ?? undefined,
  };
}

function extractAppliedField(fieldSpec?: FieldSpec): CalcResult['appliedField'] {
  const a = fieldSpec?.attackerSide ?? {};
  const d = fieldSpec?.defenderSide ?? {};
  return {
    weather: String(fieldSpec?.weather ?? ''),
    terrain: String(fieldSpec?.terrain ?? ''),
    gameType: fieldSpec?.gameType ?? 'Doubles',
    attackerSide: {
      reflect: Boolean(a.isReflect),
      lightScreen: Boolean(a.isLightScreen),
      auroraVeil: Boolean(a.isAuroraVeil),
      friendGuard: Boolean(a.isFriendGuard),
      helpingHand: Boolean(a.isHelpingHand),
    },
    defenderSide: {
      reflect: Boolean(d.isReflect),
      lightScreen: Boolean(d.isLightScreen),
      auroraVeil: Boolean(d.isAuroraVeil),
      friendGuard: Boolean(d.isFriendGuard),
      helpingHand: Boolean(d.isHelpingHand),
    },
  };
}

// Map @pkmn/dex's damageTaken codes (0=neutral, 1=2x, 2=0.5x, 3=immune) to
// multipliers. The codes are quirky — `1` is 2× super-effective and `2` is
// 0.5× resisted, which is counterintuitive — so this table makes the
// transform explicit and grep-able.
const DAMAGE_TAKEN_TO_MULTIPLIER: Record<number, number> = {
  0: 1.0,
  1: 2.0,
  2: 0.5,
  3: 0.0,
};

function computeEffectiveness(
  move: Move,
  defender: Pokemon,
  defenderSpec: PokemonSpec,
): CalcResult['effectiveness'] {
  const moveType = String(move.type ?? '');
  // Terastallized defenders use ONLY the tera type for defensive calcs. When
  // teraType is absent, use the defender's resolved types (which already
  // honors the Champions mega overlay via @smogon/calc's Pokemon override).
  const defenderTypes: string[] = defenderSpec.teraType
    ? [defenderSpec.teraType]
    : (defender.types as unknown as string[]).slice();
  let multiplier = 1;
  for (const t of defenderTypes) {
    const tdata = Dex.types.get(t);
    const code = tdata?.damageTaken?.[moveType];
    if (typeof code === 'number') {
      multiplier *= DAMAGE_TAKEN_TO_MULTIPLIER[code] ?? 1;
    }
  }
  const label: CalcResult['effectiveness']['label'] =
    multiplier === 0
      ? 'immune'
      : multiplier < 1
        ? 'resisted'
        : multiplier > 1
          ? 'super_effective'
          : 'neutral';
  return { multiplier, label, moveType, defenderTypes };
}
